import { Box, Chip, Stack, Tooltip, Typography } from "@mui/material";
import moment from "moment";
import { Submission } from "../../types/submissions";
import { User } from "../../types/users";

interface Props {
  submission: Submission;
  users: User[];
  onDragStart: (id: string) => void;
  onClick: (submission: Submission) => void;
}

function SubmissionsKanbanCard({ submission, users, onDragStart, onClick }: Props) {
  const employee = users?.find((u) => u._id === submission.employee);
  const initials = employee?.username?.slice(0, 2).toUpperCase() ?? "–";

  return (
    <Box
      draggable
      onDragStart={() => onDragStart(submission._id)}
      onClick={() => onClick(submission)}
      sx={{
        bgcolor: "white",
        border: "1px solid #E2E8F0",
        borderRadius: "6px",
        p: 1.5,
        cursor: "grab",
        "&:active": { cursor: "grabbing" },
        "&:hover": { boxShadow: "0 2px 8px rgba(15,23,42,0.08)", borderColor: "#CBD5E1" },
        transition: "box-shadow 0.1s ease, border-color 0.1s ease",
      }}
    >
      <Stack spacing={0.75}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={0.5}>
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{
              lineHeight: 1.3,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              flex: 1,
            }}
          >
            {submission.fullName}
          </Typography>
          <Tooltip title={employee?.username ?? "Unassigned"} placement="top">
            <Box
              sx={{
                width: 22,
                height: 22,
                borderRadius: "50%",
                bgcolor: "#0F172A",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Typography sx={{ color: "white", fontSize: "0.55rem", fontWeight: 700 }}>
                {initials}
              </Typography>
            </Box>
          </Tooltip>
        </Stack>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontFamily: "monospace", fontSize: "0.65rem" }}
        >
          {submission.refNo}
        </Typography>

        <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
          {submission.degree && (
            <Chip
              label={`${submission.degree}${submission.program ? ` · ${submission.program}` : ""}`}
              size="small"
              sx={{ fontSize: "0.6rem", height: 18, borderRadius: "3px", maxWidth: 160 }}
            />
          )}
          <Typography variant="caption" sx={{ fontSize: "0.6rem", color: "#64748B" }}>
            {moment(submission.createdAt).fromNow()}
          </Typography>
        </Stack>
      </Stack>
    </Box>
  );
}

export default SubmissionsKanbanCard;
